import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Switch from '@material-ui/core/Switch';
import { createStructuredSelector } from 'reselect';
import { withStyles } from '@material-ui/core/styles';

import CircularProgress from 'material-ui/CircularProgress';
import { Grid } from '@material-ui/core';
import styles from './styles';
import DataTable from '../DataTable';
import {
  selectEmailNotificationSettingsUpdatingStatus,
  selectSMSNotificationSettingsUpdatingStatus,
} from '../../store/selectors';
import { updateNotificationSettings } from '../../store/actions/settings';
import { setUserProfileCustomAttribute } from '../../store/actions/userProfile';
import { isEmptyString } from '../../utilities/helpers';
import UserInfoApi from '../UserInfoApi';
import { COLORS } from '../../constants';

export const NOTIFICATION_STATUSES = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

const propTypes = {
  classes: PropTypes.object.isRequired,
  userAttributes: PropTypes.shape({
    'custom:emailNotiStatus':
      PropTypes.oneOf[
        (NOTIFICATION_STATUSES.ENABLED, NOTIFICATION_STATUSES.DISABLED)
      ],
    'custom:smsNotiStatus':
      PropTypes.oneOf[
        (NOTIFICATION_STATUSES.ENABLED, NOTIFICATION_STATUSES.DISABLED)
      ],
  }).isRequired,
  onUpdateNotificationSettings: PropTypes.func.isRequired,
  onSetUserProfileCustomAttribute: PropTypes.func.isRequired,
  SMSNotificationSettingsUpdatingStatus: PropTypes.bool.isRequired,
};

export const NOTIFICATION_TYPES = {
  EMAIL: 'email',
  SMS: 'SMS',
};

export const getCustomAttributeKeyForNotificationType = notificationType => {
  switch (notificationType) {
    case NOTIFICATION_TYPES.EMAIL:
      return 'custom:emailNotiStatus';
    case NOTIFICATION_TYPES.SMS:
      return 'custom:smsNotiStatus';
    default:
      // eslint-disable-next-line no-console
      console.warn('Reach unknown notification type');
      return 'invalidKey';
  }
};

export const getNextNofificationStatus = currentStatus => {
  if (currentStatus === NOTIFICATION_STATUSES.ENABLED) {
    return NOTIFICATION_STATUSES.DISABLED;
  }
  return NOTIFICATION_STATUSES.ENABLED;
};

export const NotificationSettingsContainer = props => {
  const {
    classes,
    userAttributes,
    onUpdateNotificationSettings,
    onSetUserProfileCustomAttribute,
    SMSNotificationSettingsUpdatingStatus,
  } = props;

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);
  useEffect(() => {
    setEmailEnabled(
      userAttributes['custom:emailNotiStatus'] ===
        NOTIFICATION_STATUSES.ENABLED,
    );
    setSmsEnabled(
      userAttributes['custom:smsNotiStatus'] === NOTIFICATION_STATUSES.ENABLED,
    );
  }, [userAttributes]);

  useEffect(() => {
    // If the builder enables SMS notification before, but then later on he removes his mobile number from his profile,
    // we need to turn off SMS notification if it has not been turned off. This acts as a second layer of safe guard.
    // The first layer of safe guard needs to be put in place where we update the user profile, if the mobile number field is empty,
    // we set custom:mobileNumber to be 'Disabled'.
    if (
      !builderHasMobileNumber() &&
      !SMSNotificationSettingsUpdatingStatus &&
      userAttributes['custom:smsNotiStatus'] === NOTIFICATION_STATUSES.ENABLED
    ) {
      onSetUserProfileCustomAttribute(
        'custom:smsNotiStatus',
        NOTIFICATION_STATUSES.DISABLED,
        userAttributes.sub,
      );

      onUpdateNotificationSettings(
        localStorage.getItem('accessToken'),
        'custom:smsNotiStatus',
        NOTIFICATION_TYPES.SMS,
        NOTIFICATION_STATUSES.DISABLED,
        userAttributes.sub,
      );
    }
  });

  const columns = [
    {
      index: 0,
      title: 'Notification',
      name: 'notificationFullName',
      sort: {
        enabled: false,
      },
    },
    {
      index: 1,
      title: 'Status',
      name: 'status',
      sort: {
        enabled: false,
      },
      cell: row => (
        <Switch
          color="primary"
          checked={
            row.notificationType === NOTIFICATION_TYPES.EMAIL
              ? emailEnabled
              : smsEnabled
          }
          onChange={e => {
            console.log(e.target);
            handleStatusChange(row.notificationType);
            if (row.notificationType === NOTIFICATION_TYPES.EMAIL) {
              setEmailEnabled(e.target.checked);
            } else {
              setSmsEnabled(e.target.checked);
            }
          }}
          value={`${row.notificationType} notifications enabled`}
          disabled={shouldDislableSwitchToggle(row.notificationType)}
        />
      ),
    },
    {
      index: 2,
      title: 'Description',
      name: 'description',
      sort: {
        enabled: false,
      },
      cell: row => {
        if (row.notificationType === NOTIFICATION_TYPES.SMS) {
          if (!builderHasMobileNumber()) {
            return row.descriptionAlternative;
          }
        }
        // eslint-disable-next-line react/no-danger
        return row.description;
      },
    },
  ];

  const data = [
    {
      notificationType: NOTIFICATION_TYPES.EMAIL,
      notificationFullName: 'Email notifications',
      description: (
        <div>
          Email notification will be sent to{' '}
          <strong>{userAttributes['custom:personalEmail']}</strong> when a new
          job enquiry is received.
        </div>
      ),
    },
    {
      notificationType: NOTIFICATION_TYPES.SMS,
      notificationFullName: 'SMS notifications',
      description: (
        <div>
          SMS notification will be sent to{' '}
          <strong>{userAttributes['custom:mobileNumber']}</strong> when a new
          job enquiry is received.
        </div>
      ),
      descriptionAlternative: (
        <div>
          To enable SMS notifications please provide your mobile number in
          myHardies Profile
        </div>
      ),
    },
  ];

  const shouldDislableSwitchToggle = notificationType => {
    switch (notificationType) {
      case NOTIFICATION_TYPES.EMAIL:
        return false;
      case NOTIFICATION_TYPES.SMS: {
        if (!builderHasMobileNumber()) return true;
        return SMSNotificationSettingsUpdatingStatus;
      }
      default:
        return true;
    }
  };

  const builderHasMobileNumber = () => {
    const mobileNumber = userAttributes['custom:mobileNumber'];

    return mobileNumber !== undefined && !isEmptyString(mobileNumber);
  };

  const checkiFNotificationEnabled = notificationType => {
    let notificationStatus = null;
    if (!userAttributes) return false;
    switch (notificationType) {
      case NOTIFICATION_TYPES.EMAIL:
        notificationStatus = userAttributes['custom:emailNotiStatus'];
        break;
      case NOTIFICATION_TYPES.SMS:
        notificationStatus = userAttributes['custom:smsNotiStatus'];
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Reach unknown notification type');
    }

    switch (notificationStatus) {
      case NOTIFICATION_STATUSES.ENABLED:
        return true;
      case NOTIFICATION_STATUSES.DISABLED:
        return false;
      default:
        // eslint-disable-next-line no-console
        console.warn('Reach unknown notification status');
        return false;
    }
  };

  const handleStatusChange = notificationType => {
    const customAttributeKey = getCustomAttributeKeyForNotificationType(
      notificationType,
    );

    const curretStatus = checkiFNotificationEnabled(notificationType)
      ? NOTIFICATION_STATUSES.ENABLED
      : NOTIFICATION_STATUSES.DISABLED;

    const nextNofificationStatus = getNextNofificationStatus(curretStatus);

    onSetUserProfileCustomAttribute(
      customAttributeKey,
      nextNofificationStatus,
      userAttributes.sub,
    );

    onUpdateNotificationSettings(
      localStorage.getItem('accessToken'),
      customAttributeKey,
      notificationType,
      nextNofificationStatus,
      userAttributes.sub,
    );
  };

  return (
    <div className={classes.root}>
      <div className={classes.innerWrapper}>
        <Grid container alignItems="center" direction="column" justify="center">
          <Grid item xs={3}>
            {!userAttributes.sub && <CircularProgress color={COLORS.GREEN} />}
          </Grid>
        </Grid>
        {userAttributes.sub && (
          <>
            <div className={classes.introText}>
              Please select how you want to receive notifications below. You can
              update these settings at any time.
            </div>
            <DataTable columns={columns} data={data} sticky={false} />
          </>
        )}
      </div>
    </div>
  );
};

NotificationSettingsContainer.propTypes = propTypes;

const mapStateToProps = createStructuredSelector({
  // userAttributes: selectUserAttributes(),
  emailNotificationSettingsUpdatingStatus: selectEmailNotificationSettingsUpdatingStatus(),
  SMSNotificationSettingsUpdatingStatus: selectSMSNotificationSettingsUpdatingStatus(),
});

const mapDispatchToProps = dispatch => ({
  onUpdateNotificationSettings: (
    accessToken,
    customAttributeKey,
    notificationType,
    value,
    businessNumber,
  ) =>
    dispatch(
      updateNotificationSettings(
        accessToken,
        customAttributeKey,
        notificationType,
        value,
        businessNumber,
      ),
    ),
  onSetUserProfileCustomAttribute: (key, value, businessNumber) =>
    dispatch(setUserProfileCustomAttribute(key, value, businessNumber)),
});

export default UserInfoApi(
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(NotificationSettingsContainer),
  ),
);
